"use client"

// import { useState } from "react"
import { Play, Download, Link, Trash2, RefreshCcw } from "lucide-react"
import { Video } from "../../context/VideoContext";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { VideoStatus } from "../../services/interface";
import { convertToZone, formatDateToShort } from "../../utils/formatters";
import { toast } from "react-hot-toast";
import { deleteVideo } from "../../services/videos/videosService";
import Loader from "../common/Loader";
import { useState } from "react";
import { useEffect } from "react";

interface VideoPlayerProps {
    onCreateAd: () => void;
    video: Video | undefined;
    updateInfoVideo: (id: string) => void;
}

export default function VideoPlayer({ video, onCreateAd, updateInfoVideo }: VideoPlayerProps) {
    const { language } = useAuth();
    const [isDownloading, setIsDownloading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    if (video === undefined) {
        return <div>{dictionary["no-video-found"][language]}</div>
    }

    const handleStylesStatusVideo = (status: VideoStatus) => {
        switch (status) {
            case VideoStatus.INITIALIZING:
                return "bg-yellow-500"
            case VideoStatus.IN_PROGRESS:
                return "bg-blue-500"
            case VideoStatus.DONE:
                return "bg-green-500"
            case VideoStatus.FAILED:
                return "bg-red-500"
        }
    }

    const handlePlay = () => {
        window.open(video.video_url, '_blank');
    }

    const handleDownload = async () => {
        try {
            setIsDownloading(true)
            if (!video.video_url) {
                toast.error(dictionary["video-not-found"][language])
                return
            }

            const response = await fetch(video.video_url);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = video.video_url.split('/').pop() || 'video.mp4';
            a.click();
            URL.revokeObjectURL(url);
            setIsDownloading(false)
        } catch (error) {
            console.error('Error al descargar el video:', error);
        }
    };

    const handleCopyLink = () => {
        if (video.video_url) {
            navigator.clipboard.writeText(video.video_url)
            toast.success(dictionary["link-copied"][language])
        }
    }

    const handleDelete = () => {
        console.log("handleDelete", video.id)
        setIsDeleting(true)
        deleteVideo(video.id.toString())
            .then(() => {
                toast.success(dictionary["video-deleted"][language])
            })
            .catch((error) => {
                console.error(error)
                toast.error(dictionary["video-deleted-error"][language])
            })
            .finally(() => {
                updateInfoVideo(video.id.toString())
                setIsDeleting(false)
            })
    }

    const handleRetry = async () => {
        await handleDelete()
        await onCreateAd()
    }

    useEffect(() => {
        if (video.state === VideoStatus.DONE) {
            updateInfoVideo(video.id.toString())
        }
    }, [video.state])

    return (
        <div className="space-y-6">
            {/* Status Header */}
            <div className="flex items-center gap-2">
                <div className={`w-3 h-3 ${handleStylesStatusVideo(video.state)} rounded-full`}></div>
                <span className="text-gray-600 font-medium">{dictionary["status"][language]}: {dictionary[video.state][language]}</span>
                <span className="text-gray-400">{formatDateToShort(video.created_at)}</span>
            </div>

            {/* Video Container */}
            {video.state !== VideoStatus.FAILED && (
                <div className="w-full h-[300px]">
                    {video.video_url ? (
                        <video className="w-full h-full rounded-2xl" src={video.video_url} controls />
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <Loader />
                        </div>
                    )}
                </div>
            )}

            {/* Action Buttons */}
            {video.state === VideoStatus.DONE ? (
                <div className="flex flex-wrap gap-4">
                    <button
                        disabled={video.state !== VideoStatus.DONE}
                        onClick={handlePlay}
                        className="bg-blue-500 hover:bg-blue-600 text-white rounded-2xl px-6 py-3 font-medium shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
                    >
                        <Play className="w-4 h-4 mr-2" />
                        Ver video
                    </button>

                    <button
                        disabled={video.state !== VideoStatus.DONE || isDownloading}
                        onClick={handleDownload}
                        className="bg-green-500 hover:bg-green-600 text-white rounded-2xl px-6 py-3 font-medium shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
                    >
                        {isDownloading ? <Loader /> : <><Download className="w-4 h-4 mr-2" /> Descargar</>}
                    </button>

                    <button
                        disabled={video.state !== VideoStatus.DONE}
                        onClick={handleCopyLink}
                        className="bg-gray-600 hover:bg-gray-700 text-white rounded-2xl px-6 py-3 font-medium shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
                    >
                        <Link className="w-4 h-4 mr-2" />
                        Copiar link
                    </button>

                    <button
                        disabled={video.state !== VideoStatus.DONE || isDeleting}
                        onClick={handleDelete}
                        className="bg-red-500 hover:bg-red-600 text-white rounded-2xl px-6 py-3 font-medium shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
                    >
                        {isDeleting ? <Loader /> : <><Trash2 className="w-4 h-4 mr-2" /> Eliminar</>}
                    </button>
                </div>
            ) : video.state === VideoStatus.FAILED ? (
                <div className="flex justify-center">
                    <button
                        disabled={video.state !== VideoStatus.FAILED}
                        onClick={handleRetry}
                        className="bg-gray-600 hover:bg-gray-700 text-white rounded-2xl px-6 py-3 font-medium shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
                    >
                        <RefreshCcw className="w-4 h-4 mr-2" />
                        Reintentar
                    </button>
                </div>) : null}
        </div>
    )
}
